import { createContext, useContext, useReducer, useEffect, useState, ReactNode } from 'react';
import { AuthContextType, AuthState, LoginRequest, SignupRequest, User } from '../types/auth';
import { authService } from '../services/authService';
import { QueryClient } from '@tanstack/react-query';
import { useGraphStore } from '../stores/graphStore';

// Initial state
const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Action types
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'AUTH_INIT_COMPLETE' };

// Reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    case 'AUTH_INIT_COMPLETE':
      return {
        ...state,
        isLoading: false,
      };
    default:
      return state;
  }
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
export function AuthProvider({ children, queryClient }: { children: ReactNode; queryClient?: QueryClient }) {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load token from localStorage on mount (refresh access JWT via refresh token when expired)
  useEffect(() => {
    const loadAuthData = async () => {
      const clearStoredAuth = () => {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        localStorage.removeItem('auth_refresh_token');
      };

      const token = localStorage.getItem('auth_token');
      const userStr = localStorage.getItem('auth_user');

      if (token && userStr && token.trim() !== '') {
        try {
          JSON.parse(userStr);
          try {
            const response = await authService.getCurrentUser();
            dispatch({
              type: 'AUTH_SUCCESS',
              payload: { user: response, token: localStorage.getItem('auth_token')! },
            });
          } catch {
            const refreshed = await authService.tryRefresh();
            if (refreshed) {
              try {
                const response = await authService.getCurrentUser();
                dispatch({
                  type: 'AUTH_SUCCESS',
                  payload: { user: response, token: localStorage.getItem('auth_token')! },
                });
              } catch {
                clearStoredAuth();
                dispatch({ type: 'AUTH_FAILURE', payload: 'Token validation failed' });
              }
            } else {
              clearStoredAuth();
              dispatch({ type: 'AUTH_FAILURE', payload: 'Token validation failed' });
            }
          }
        } catch {
          clearStoredAuth();
          dispatch({ type: 'AUTH_FAILURE', payload: 'Invalid stored authentication data' });
        }
      } else {
        clearStoredAuth();
        dispatch({ type: 'AUTH_FAILURE', payload: '' });
      }
      
      // Mark initialization as complete
      dispatch({ type: 'AUTH_INIT_COMPLETE' });
      setIsInitialized(true);
    };
    
    loadAuthData();
  }, []);

  // Save to localStorage when auth state changes (only after initialization)
  useEffect(() => {
    if (!isInitialized) {
      return;
    }
    
    if (state.token && state.user) {
      localStorage.setItem('auth_token', state.token);
      localStorage.setItem('auth_user', JSON.stringify(state.user));
    } else if (!state.isAuthenticated) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      localStorage.removeItem('auth_refresh_token');
    }
  }, [state.token, state.user, state.isAuthenticated, isInitialized]);

  const login = async (credentials: LoginRequest) => {
    dispatch({ type: 'AUTH_START' });
    try {
      const response = await authService.login(credentials);
      if (response.refresh_token) {
        localStorage.setItem('auth_refresh_token', response.refresh_token);
      }
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user: response.user!, token: response.access_token },
      });
    } catch (error) {
      dispatch({
        type: 'AUTH_FAILURE',
        payload: error instanceof Error ? error.message : 'Login failed',
      });
    }
  };

  const signup = async (credentials: SignupRequest | any) => {
    dispatch({ type: 'AUTH_START' });
    try {
      let response;
      
      // Check if this is an invite-based signup
      if (credentials.invite_token) {
        const { inviteService } = await import('../services/inviteService');
        response = await inviteService.signupWithInvite(credentials);
      } else {
        response = await authService.signup(credentials);
      }
      
      if (response.refresh_token) {
        localStorage.setItem('auth_refresh_token', response.refresh_token);
      }
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user: response.user!, token: response.access_token },
      });
    } catch (error) {
      dispatch({
        type: 'AUTH_FAILURE',
        payload: error instanceof Error ? error.message : 'Signup failed',
      });
    }
  };

  const loginWithApple = async () => {
    dispatch({ type: 'AUTH_START' });
    try {
      const response = await authService.signInWithApple();
      if (response.refresh_token) {
        localStorage.setItem('auth_refresh_token', response.refresh_token);
      }
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user: response.user!, token: response.access_token },
      });
    } catch (error) {
      dispatch({
        type: 'AUTH_FAILURE',
        payload: error instanceof Error ? error.message : 'Apple Sign-In failed',
      });
    }
  };

  const logout = () => {
    void authService.notifyLogout();
    dispatch({ type: 'LOGOUT' });
    // Clear React Query cache to prevent data from persisting after logout
    if (queryClient) {
      queryClient.clear();
    }
    // Clear graph store data
    const { clearGraph } = useGraphStore.getState();
    clearGraph();
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const value: AuthContextType = {
    ...state,
    login,
    signup,
    loginWithApple,
    logout,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 